import React from "react";
import PropTypes from 'prop-types';

interface TabRowProps extends React.HTMLAttributes<HTMLElement> {
    tabTitles?: Array<string>;
    tabIDs?: Array<string>;
    tabHelpTexts?: Array<string>;
    activeTab?: string;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
    // Functions
    onTabChange?: (id: string) => void;
}

/**
 * A row of tabs, used to split a partition of your application into pieces.
 * Note that this is just the tabs, not any children of said tabs.
 * 
 * ## _
 * ### Arguments
 * @param {Array<string>} tabTitles - An array of titles for each tab, to be displayed.
 * @param {Array<string>} tabIDs - An optional array of unique identifiers for each tab. If these are not provided, the title will be used instead.
 * @param {Array<string>} tabHelpTexts - An optional array for subtext on each tab. Leave blanks with empty strings.
 * @param {Array<string>} activeTab - The ID (or title) of the currently active tab. This should be a state you manage.
 * @param {React.ReactNode} children - The contents inside the tab row. This is usually just text.
 * @param {string} className - CSS classes to apply to the tab row.
 * 
 * ## _
 * ### Functions
 * @param {function} onTabChange - When a tab is clicked, this function will trigger, passing its' identifier. This should be used to update the `activeTab` state.
 */
class TabRow extends React.Component<TabRowProps> {
    static propTypes = {
        tabTitles: PropTypes.arrayOf(PropTypes.string),
        tabIDs: PropTypes.arrayOf(PropTypes.string),
        tabHelpTexts: PropTypes.arrayOf(PropTypes.string),
        activeTab: PropTypes.string,
        children: PropTypes.node,
        className: PropTypes.string,
        onTabChange: PropTypes.func,
    }

    static defaultProps = {
        className: "",
        tabTitles: [],
        tabIDs: [],
        tabHelpTexts: [],
        activeTab: "",
    }

    render() {
        const { tabTitles = [], tabIDs = [], tabHelpTexts = [], activeTab, children, className, onTabChange, ...rest } = this.props;

        let identifiers = tabTitles.map((title, index) => {
            if (index < tabIDs.length && tabIDs[index] !== "") {
                return tabIDs[index];
            }
            return title;
        })

        return (
            <div className={`m-tab-row ${className}`} {...rest}>
                {tabTitles.map((title, index) => (
                    <button 
                        onClick={() => onTabChange?.(identifiers[index])} 
                        className={`m-tab-row__tab${identifiers[index] === activeTab ? ` m-tab-row__tab--active` : ``}`}
                    >
                        <span className="m-tab-row__tab-title">{title}</span>
                        {tabHelpTexts.length > index && <span className="m-tab-row__help">{tabHelpTexts[index]}</span>}
                    </button>
                ))}
            </div>
        );
    }
}

export default TabRow;